
/**
 * Utility functions for image optimization
 */

// Cache for WebP support check to avoid repeated checks
let webpSupportCache: Promise<boolean> | null = null;

/**
 * Preloads critical images to improve performance
 * @param imageSources Array of image URLs to preload
 */
export const preloadCriticalImages = (imageSources: string[]) => {
  imageSources.forEach(src => {
    if (!src) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.type = getImageMimeType(src);
    
    document.head.appendChild(link);
  });
};

/**
 * Get image MIME type based on URL or extension
 */
const getImageMimeType = (src: string): string => {
  if (src.includes('.webp')) return 'image/webp';
  if (src.includes('.png')) return 'image/png';
  if (src.includes('.jpg') || src.includes('.jpeg')) return 'image/jpeg';
  if (src.includes('.gif')) return 'image/gif';
  if (src.includes('.svg')) return 'image/svg+xml';
  return 'image/jpeg'; // Default
};

/**
 * Preloads an array of images to improve performance
 * @param imageSources Array of image URLs to preload
 */
export const preloadImages = (imageSources: string[]) => {
  imageSources.forEach(src => {
    if (!src) return;
    const img = new Image();
    img.src = src;
  });
};

/**
 * Checks if the browser supports WebP format
 * @returns Promise that resolves to a boolean indicating WebP support
 */
export const supportsWebP = async (): Promise<boolean> => {
  // Use cached result if available
  if (webpSupportCache) {
    return webpSupportCache;
  }
  
  // Create a new promise and cache it
  webpSupportCache = new Promise<boolean>((resolve) => {
    if (!self.createImageBitmap) {
      resolve(false);
      return;
    }
    
    const webpData = 'data:image/webp;base64,UklGRh4AAABXRUJQVlA4TBEAAAAvAAAAAAfQ//73v/+BiOh/AAA=';
    fetch(webpData)
      .then(r => r.blob())
      .then(blob => createImageBitmap(blob))
      .then(() => resolve(true))
      .catch(() => resolve(false));
  });
  
  return webpSupportCache;
};

/**
 * Generates a responsive image URL based on screen size
 * @param src Original image URL
 * @param width Desired width
 * @returns Optimized image URL
 */
export const getResponsiveImageUrl = (src: string, width: number): string => {
  if (!src) return '';
  
  // If it's already a data URL, return as is
  if (src.startsWith('data:')) return src;
  
  // If it's a relative URL, add origin
  if (src.startsWith('/') && !src.startsWith('//')) {
    src = window.location.origin + src;
  }
  
  try {
    // Handle local project images from lovable-uploads directly
    if (src.includes('lovable-uploads')) {
      // Can't transform these, return as is
      return src;
    }
    
    // If it's an external URL with image optimization parameters, return as is
    if (src.includes('w=') || src.includes('width=') || src.includes('size=')) {
      return src;
    }
    
    // Check if the URL is from common image CDNs that support on-the-fly resizing
    
    // Cloudinary
    if (src.includes('cloudinary.com')) {
      // Extract base path and filename from the URL
      const match = src.match(/\/upload\/(.*)\/v\d+\/(.*)$/);
      if (match) {
        const [_, options, filename] = match;
        const optimizedOptions = `c_limit,w_${width},q_auto,f_auto`;
        return src.replace(`/upload/${options}/`, `/upload/${optimizedOptions}/`);
      }
    }
    
    // Unsplash
    if (src.includes('unsplash.com') && !src.includes('w=')) {
      return `${src}${src.includes('?') ? '&' : '?'}w=${width}&q=80&auto=format`;
    }
  
    // Imgur
    if (src.includes('imgur.com') && !src.includes('.webp')) {
      return src.replace(/\.(jpg|jpeg|png)/, '.webp');
    }
  } catch (error) {
    console.error('Error optimizing image URL:', error);
    return src;
  }
  
  // If no optimization can be applied, return the original URL
  return src;
};

/**
 * Applies a blur-up technique, returning a tiny placeholder and the full image
 * @param src Image URL
 * @returns Object with placeholder and full image URLs
 */
export const getBlurUpImageData = (src: string) => {
  // For Unsplash images, we can use their built-in blur hash
  if (src.includes('unsplash.com')) {
    const placeholder = `${src}${src.includes('?') ? '&' : '?'}w=20&blur=25`;
    const fullImage = `${src}${src.includes('?') ? '&' : '?'}w=1080&q=80`;
    return { placeholder, fullImage };
  }
  
  // For other images, return the original URL
  return {
    placeholder: src,
    fullImage: src
  };
};

/**
 * Convert image to WebP if possible and return URL
 * @param src Original image URL
 * @returns WebP version of the image if possible, otherwise original
 */
export const convertToWebP = (src: string): string => {
  if (!src) return '';
  
  try {
    // For Unsplash images
    if (src.includes('unsplash.com')) {
      return `${src}${src.includes('?') ? '&' : '?'}fm=webp`;
    }
    
    // For Imgur images
    if (src.includes('imgur.com') && !src.includes('.webp')) {
      return src.replace(/\.(jpg|jpeg|png)/, '.webp');
    }
    
    // For Cloudinary images
    if (src.includes('cloudinary.com')) {
      return src.replace('/upload/', '/upload/f_webp,q_auto/');
    }
  } catch (error) {
    console.error('Error converting to WebP:', error);
    return src;
  }
  
  // Return original if no conversion is possible
  return src;
};

/**
 * Calculates the ideal image dimensions based on container size and device pixel ratio
 */
export const calculateIdealImageDimensions = (
  containerWidth: number,
  containerHeight?: number,
  aspectRatio = 16/9
): { width: number, height: number } => {
  const dpr = window.devicePixelRatio || 1;
  const idealWidth = Math.round(containerWidth * dpr);
  
  // If height is provided, use it; otherwise calculate from aspect ratio
  const idealHeight = containerHeight 
    ? Math.round(containerHeight * dpr)
    : Math.round(idealWidth / aspectRatio);
    
  return {
    width: idealWidth,
    height: idealHeight
  };
};

/**
 * Get srcset string for responsive images
 */
export const getSrcSetString = (src: string, sizes = [320, 640, 960, 1280, 1920]): string => {
  if (!src || src.startsWith('data:')) return '';
  
  return sizes.map(size => {
    const url = getResponsiveImageUrl(src, size);
    return `${url} ${size}w`;
  }).join(', ');
};
